import { BusinessObjectBase } from './base';
import { salePhotoSchema } from '@schemas/business-objects/sale-photo';
import type { MeasurementSet } from './measurement-set';
import type { Sale } from './sale';
import type { Id, Sex } from '../base-types';

export interface SalePhotoData {
  readonly id: Id;
  readonly hipNumber: string;
  readonly sex?: Sex;
  readonly sire?: string;
  readonly dam?: string;
  readonly consignor?: string;
  readonly imageData?: string;
  readonly contentType?: string;
  readonly imageWidth?: number;
  readonly imageHeight?: number;
  readonly saleId: Id;
  readonly sale?: Sale;
  readonly measurementSets?: MeasurementSet[];
}

export interface SalePhoto extends Readonly<SalePhotoData> {}
export class SalePhoto extends BusinessObjectBase {
  static readonly entityName = 'SalePhoto' as const;
  static readonly plural = 'sale-photos';
  static schema = salePhotoSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<SalePhoto[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new SalePhoto(data));
  }

  static async findOne(filter?: any): Promise<SalePhoto | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new SalePhoto(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<SalePhoto> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new SalePhoto(data);
  }

  static async create(data: Omit<SalePhotoData, 'id'>): Promise<SalePhoto> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new SalePhoto(result);
  }

  static async createMany(
    data: Omit<SalePhotoData, 'id'>[],
  ): Promise<SalePhoto[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SalePhoto(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<SalePhotoData>,
  ): Promise<SalePhoto> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new SalePhoto(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async extractBannerDetails(overwrite?: boolean): Promise<SalePhoto> {
    const url = `${BusinessObjectBase.baseUrl}/${SalePhoto.plural}/${(this as any).id}/extract-banner-details`;
    return BusinessObjectBase.post<SalePhoto>(url, { overwrite });
  }
}
